<template>
  <transition name="popup-fade">
    <div
      v-if="visible"
      :class="['popup-overlay', isFullscreen ? 'popup-overlay--fs' : '']"
      @click.self="close"
    >

      <!-- ══════════════════════════════════════════════════════════
           FULLSCREEN  — split-screen hero layout
      ══════════════════════════════════════════════════════════ -->
      <div
        v-if="isFullscreen"
        :class="['fs-popup', `fs-popup--${popup.popup_type}`]"
        role="dialog"
        aria-modal="true"
      >
        <!-- Close -->
        <button class="fs-close" @click="close" aria-label="Close">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Left: image / video pane -->
        <div v-if="popup.image_url || popup.video_url" class="fs-media">
          <img v-if="popup.image_url" :src="popup.image_url" class="fs-img" alt="" />
          <div v-else class="fs-video-wrap">
            <iframe
              :src="popup.video_url"
              frameborder="0"
              allow="autoplay; encrypted-media"
              allowfullscreen
              class="fs-iframe"
            ></iframe>
          </div>
          <!-- gradient overlay at bottom of image -->
          <div :class="['fs-media-gradient', `fs-media-gradient--${popup.popup_type}`]" />
        </div>

        <!-- Right: content pane -->
        <div :class="['fs-content', !popup.image_url && !popup.video_url ? 'fs-content--full' : '']">
          <div class="fs-inner">
            <!-- Badge -->
            <div :class="['fs-badge', `fs-badge--${popup.popup_type}`]">
              {{ typeLabelMap[popup.popup_type] || popup.popup_type }}
            </div>

            <h2 v-if="popup.title"    class="fs-title">{{ popup.title }}</h2>
            <p  v-if="popup.subtitle" class="fs-subtitle">{{ popup.subtitle }}</p>
            <div v-if="popup.body"    class="fs-text" v-html="popup.body" />

            <a
              v-if="popup.button_text"
              :href="popup.button_url || '#'"
              :class="['fs-cta', `fs-cta--${popup.popup_type}`]"
              @click="close"
            >
              {{ popup.button_text }}
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>

          <!-- Decorative brand accent bar at top of content pane -->
          <div :class="['fs-accent', `fs-accent--${popup.popup_type}`]" />
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════
           STANDARD  — card popup (small / medium / large)
      ══════════════════════════════════════════════════════════ -->
      <div
        v-else
        :class="['popup-box', `popup-box--${popup.popup_type}`, `popup-box--size-${popup.size || 'medium'}`]"
        role="dialog"
        aria-modal="true"
      >
        <!-- Close button -->
        <button v-if="popup.show_close_btn !== false" class="popup-close" @click="close" aria-label="Close">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Image -->
        <img
          v-if="popup.image_url"
          :src="popup.image_url"
          class="popup-img"
          alt=""
          loading="lazy"
        />

        <!-- Video embed -->
        <div v-else-if="popup.video_url" class="popup-video-wrap">
          <iframe
            :src="popup.video_url"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="popup-iframe"
          ></iframe>
        </div>

        <!-- Type badge -->
        <div :class="['popup-badge', `popup-badge--${popup.popup_type}`]">
          <span>{{ typeLabelMap[popup.popup_type] || popup.popup_type }}</span>
        </div>

        <!-- Content -->
        <div class="popup-body">
          <h2 v-if="popup.title"    class="popup-title">{{ popup.title }}</h2>
          <p  v-if="popup.subtitle" class="popup-subtitle">{{ popup.subtitle }}</p>
          <div v-if="popup.body"    class="popup-text" v-html="popup.body"></div>
          <a
            v-if="popup.button_text"
            :href="popup.button_url || '#'"
            :class="['popup-cta', `popup-cta--${popup.popup_type}`]"
            @click="close"
          >
            {{ popup.button_text }}
          </a>
        </div>
      </div>

    </div>
  </transition>
</template>

<script>
const API_BASE  = (process.env.VUE_APP_API_BASE || 'http://localhost:8002/api').replace(/\/+$/, '');
const STORE_KEY = 'sholaa_popup';

function isSuppressed(frequency) {
  const key = `${STORE_KEY}_${frequency}`;
  if (frequency === 'always')    return false;
  if (frequency === 'session')   return !!sessionStorage.getItem(key);
  if (frequency === 'permanent') return !!localStorage.getItem(key);
  if (frequency === 'daily') {
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    return stored === new Date().toISOString().slice(0, 10);
  }
  return false;
}

function markSeen(frequency) {
  const key = `${STORE_KEY}_${frequency}`;
  if (frequency === 'session')   sessionStorage.setItem(key, '1');
  if (frequency === 'permanent') localStorage.setItem(key, '1');
  if (frequency === 'daily')     localStorage.setItem(key, new Date().toISOString().slice(0, 10));
}

export default {
  name: 'AnnouncementPopup',

  data() {
    return {
      visible: false,
      popup: {},
      typeLabelMap: {
        announcement: '📢 Announcement',
        info:         'ℹ️ Info',
        warning:      '⚠️ Notice',
        event:        '🎉 Event',
      },
    };
  },

  computed: {
    isFullscreen() {
      return this.popup.size === 'fullscreen';
    },
  },

  async mounted() {
    try {
      const res  = await fetch(`${API_BASE}/popup/`);
      if (!res.ok) return;
      const data = await res.json();

      if (!data.is_active) return;

      const now = new Date();
      if (data.show_from  && new Date(data.show_from)  > now) return;
      if (data.show_until && new Date(data.show_until) < now) return;

      const freq = data.show_frequency || 'session';
      if (isSuppressed(freq)) return;

      this.popup = data;
      const delay = (data.show_after || 0) * 1000;
      setTimeout(() => { this.visible = true; }, delay);
    } catch {
      // silently fail — never break the public site
    }
  },

  methods: {
    close() {
      this.visible = false;
      markSeen(this.popup.show_frequency || 'session');
    },
  },
};
</script>

<style scoped>

/* ═══════════════════════════════════════════════════════════════════
   OVERLAY
═══════════════════════════════════════════════════════════════════ */
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  backdrop-filter: blur(4px);
}
/* Fullscreen overlay — no padding, no blur needed */
.popup-overlay--fs {
  padding: 0;
  backdrop-filter: none;
  background: rgba(0, 0, 0, 0.85);
}

/* ═══════════════════════════════════════════════════════════════════
   STANDARD CARD  (small / medium / large)
═══════════════════════════════════════════════════════════════════ */
.popup-box {
  position: relative;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 520px;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
}

/* Type accent bar */
.popup-box--announcement { border-top: 5px solid #aa4b6b; }
.popup-box--info         { border-top: 5px solid #3b82f6; }
.popup-box--warning      { border-top: 5px solid #f59e0b; }
.popup-box--event        { border-top: 5px solid #8b5cf6; }

/* Size variants */
.popup-box--size-small  { max-width: 380px; }
.popup-box--size-medium { max-width: 520px; }
.popup-box--size-large  { max-width: 700px; }

/* Close */
.popup-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  color: #374151;
  transition: all 0.15s;
}
.popup-close:hover { background: #f3f4f6; transform: scale(1.05); }
.popup-close svg   { width: 16px; height: 16px; }

/* Image */
.popup-img {
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  display: block;
}

/* Video */
.popup-video-wrap {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
}
.popup-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* Badge */
.popup-badge {
  padding: 10px 18px 0;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.popup-badge--announcement { color: #aa4b6b; }
.popup-badge--info         { color: #2563eb; }
.popup-badge--warning      { color: #d97706; }
.popup-badge--event        { color: #7c3aed; }

/* Body */
.popup-body { padding: 12px 24px 24px; }

.popup-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 6px;
  line-height: 1.3;
}
.popup-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 12px;
}
.popup-text {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 16px;
}

/* CTA */
.popup-cta {
  display: inline-block;
  padding: 11px 22px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;
}
.popup-cta--announcement { background: linear-gradient(135deg, #aa4b6b, #6b3fa0); color: #fff; }
.popup-cta--info         { background: #3b82f6; color: #fff; }
.popup-cta--warning      { background: #f59e0b; color: #fff; }
.popup-cta--event        { background: #8b5cf6; color: #fff; }
.popup-cta:hover         { transform: translateY(-1px); opacity: 0.9; }

/* ═══════════════════════════════════════════════════════════════════
   FULLSCREEN  — split-screen hero
═══════════════════════════════════════════════════════════════════ */
.fs-popup {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  animation: fs-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes fs-in {
  from { opacity: 0; transform: scale(1.03); }
  to   { opacity: 1; transform: scale(1); }
}

/* ── Media pane (left 55%) ── */
.fs-media {
  position: relative;
  flex: 0 0 55%;
  overflow: hidden;
}
.fs-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 8s ease;
}
.fs-popup:hover .fs-img { transform: scale(1.04); }
.fs-video-wrap {
  position: relative;
  width: 100%;
  height: 100%;
}
.fs-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
/* Gradient overlay at bottom of image for depth */
.fs-media-gradient {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.fs-media-gradient--announcement { background: linear-gradient(135deg, rgba(170,75,107,.35) 0%, transparent 60%), linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 50%); }
.fs-media-gradient--info         { background: linear-gradient(135deg, rgba(59,130,246,.3) 0%, transparent 60%),  linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 50%); }
.fs-media-gradient--warning      { background: linear-gradient(135deg, rgba(245,158,11,.3) 0%, transparent 60%),  linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 50%); }
.fs-media-gradient--event        { background: linear-gradient(135deg, rgba(139,92,246,.3) 0%, transparent 60%),  linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 50%); }

/* ── Content pane (right 45%) ── */
.fs-content {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #fff;
  overflow-y: auto;
}
/* When no image: center in full width */
.fs-content--full {
  align-items: center;
  text-align: center;
}

/* Top accent stripe */
.fs-accent {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 5px;
}
.fs-accent--announcement { background: linear-gradient(90deg, #aa4b6b, #6b3fa0); }
.fs-accent--info         { background: linear-gradient(90deg, #3b82f6, #06b6d4); }
.fs-accent--warning      { background: linear-gradient(90deg, #f59e0b, #ef4444); }
.fs-accent--event        { background: linear-gradient(90deg, #8b5cf6, #ec4899); }

/* Inner content */
.fs-inner {
  padding: 60px 56px;
  max-width: 520px;
}
.fs-content--full .fs-inner {
  max-width: 640px;
  padding: 80px 64px;
}

/* Badge */
.fs-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 30px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 20px;
}
.fs-badge--announcement { background: #fdf0f4; color: #aa4b6b; border: 1.5px solid #f9c4d2; }
.fs-badge--info         { background: #eff6ff; color: #2563eb; border: 1.5px solid #bfdbfe; }
.fs-badge--warning      { background: #fffbeb; color: #d97706; border: 1.5px solid #fde68a; }
.fs-badge--event        { background: #f5f3ff; color: #7c3aed; border: 1.5px solid #ddd6fe; }

/* Title */
.fs-title {
  font-size: 2.4rem;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.15;
  margin: 0 0 14px;
  letter-spacing: -0.02em;
}

/* Subtitle */
.fs-subtitle {
  font-size: 1.05rem;
  color: #64748b;
  margin: 0 0 20px;
  line-height: 1.6;
}

/* Body text */
.fs-text {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 32px;
}

/* CTA */
.fs-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.01em;
}
.fs-cta svg { width: 16px; height: 16px; transition: transform 0.2s; }
.fs-cta:hover svg { transform: translateX(3px); }
.fs-cta--announcement { background: linear-gradient(135deg, #aa4b6b, #6b3fa0); color: #fff; box-shadow: 0 8px 24px rgba(170,75,107,.35); }
.fs-cta--info         { background: linear-gradient(135deg, #3b82f6, #06b6d4); color: #fff; box-shadow: 0 8px 24px rgba(59,130,246,.35); }
.fs-cta--warning      { background: linear-gradient(135deg, #f59e0b, #ef4444); color: #fff; box-shadow: 0 8px 24px rgba(245,158,11,.35); }
.fs-cta--event        { background: linear-gradient(135deg, #8b5cf6, #ec4899); color: #fff; box-shadow: 0 8px 24px rgba(139,92,246,.35); }
.fs-cta:hover         { transform: translateY(-2px); opacity: 0.92; }

/* Close (fullscreen) */
.fs-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.15s;
  box-shadow: 0 2px 12px rgba(0,0,0,0.2);
}
.fs-close:hover { background: rgba(255,255,255,0.25); transform: scale(1.08); }
.fs-close svg   { width: 18px; height: 18px; }

/* On content pane the close btn needs dark color */
.fs-content ~ .fs-close,
.fs-popup:not(:has(.fs-media)) .fs-close {
  background: #f1f5f9;
  color: #374151;
}

/* ── Mobile: stack vertically ── */
@media (max-width: 768px) {
  .fs-popup { flex-direction: column; }
  .fs-media { flex: 0 0 45vh; }
  .fs-content { flex: 1; }
  .fs-inner { padding: 32px 28px; }
  .fs-title { font-size: 1.7rem; }
  .fs-subtitle { font-size: 0.95rem; }
}

/* ═══════════════════════════════════════════════════════════════════
   TRANSITIONS
═══════════════════════════════════════════════════════════════════ */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}
.popup-fade-enter-active .popup-box,
.popup-fade-leave-active .popup-box {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}
.popup-fade-enter-from .popup-box,
.popup-fade-leave-to .popup-box {
  transform: scale(0.94) translateY(16px);
  opacity: 0;
}

@media (max-width: 480px) {
  .popup-box { border-radius: 16px; }
  .popup-body { padding: 10px 16px 20px; }
  .popup-title { font-size: 1.1rem; }
}
</style>
